import { UserPaymentDetailsCardProps } from './props';
import styles from './styles.module.scss';
import UserPaymentDetailsCardIndicator from './Indicator';
import UserPaymentDetailsCardStatus from './Status';
import UserPaymentDetailsCardSkeleton from './Skeleton';
import { memo } from 'react';
import { Collapse } from '@mui/material';
import { useTranslation } from '../../../../../../hooks';
import { UserPaymentDetailsTypeId } from '../../../../../../enums';

/**
 * UserPaymentDetailsCard Component
 * This component is responsible for rendering a card that displays user payment details.
 * It handles different types of payment details and provides a visual representation
 * based on the selected state and loading status.
 *
 * @param {UserPaymentDetailsCardProps} props - The properties for the component.
 * @param {boolean} props.isLoading - Indicates if the data is still loading.
 * @param {object} props.userPaymentDetails - The user payment details object.
 * @param {boolean} props.selected - Indicates if the card is currently selected.
 * @param {function} props.onClick - Callback function to handle click events.
 *
 * @returns {JSX.Element} The rendered component displaying the user payment details card.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsCard
 *   isLoading={false}
 *   userPaymentDetails={{ type: { id: '1' }, pix: { maskedKey: '****' } }}
 *   selected={true}
 *   onClick={(details) => function(details)}
 * />
 */
function UserPaymentDetailsCard({
  isLoading,
  userPaymentDetails,
  selected,
  onClick,
}: UserPaymentDetailsCardProps) {
  const { t } = useTranslation('common');

  /**
   * Handles the click event on the card.
   * Calls the onClick callback with the user payment details.
   */
  function handleClick() {
    onClick?.(userPaymentDetails);
  }

  /**
   * Retrieves the value to be displayed on the card based on the payment type.
   *
   * @returns {string | undefined} The value to display, such as a masked key or account number.
   */
  function getValue(): string | undefined {
    switch (userPaymentDetails?.type?.id?.toString()) {
      case UserPaymentDetailsTypeId.Pix?.toString():
        return userPaymentDetails?.pix?.maskedKey;
      case UserPaymentDetailsTypeId.Imps?.toString():
        return userPaymentDetails?.imps?.accountNumber;
      case UserPaymentDetailsTypeId.ArgentineBankAccount?.toString():
      case UserPaymentDetailsTypeId.MexicanBankAccount?.toString():
        return userPaymentDetails?.bankAccount?.account;
      case UserPaymentDetailsTypeId.NigerianBankAccount?.toString():
      case UserPaymentDetailsTypeId.SingaporeBankAccount?.toString():
      case UserPaymentDetailsTypeId.SouthAfricaBankAccount?.toString():
        return userPaymentDetails?.bankAccount?.account;
    }
  }

  /**
   * Retrieves the label to be displayed on the card based on the payment type.
   *
   * @returns {string | undefined} The label to display, such as 'imps_label' or 'bank_account_label'.
   */
  function getLabel(): string | undefined {
    switch (userPaymentDetails?.type?.id?.toString()) {
      case UserPaymentDetailsTypeId.Pix?.toString():
        return t(`${userPaymentDetails?.pix?.type?.toLowerCase()}`);
      case UserPaymentDetailsTypeId.Imps?.toString():
        return t('imps_label');
      case UserPaymentDetailsTypeId.ArgentineBankAccount?.toString():
      case UserPaymentDetailsTypeId.MexicanBankAccount?.toString():
      case UserPaymentDetailsTypeId.NigerianBankAccount?.toString():
      case UserPaymentDetailsTypeId.SingaporeBankAccount?.toString():
      case UserPaymentDetailsTypeId.SouthAfricaBankAccount?.toString():
        return t('bank_account_label');
    }
  }

  // If the data is still loading, display a skeleton component
  if (isLoading) {
    return <UserPaymentDetailsCardSkeleton />;
  }

  // Render the user payment details card
  return (
    <div
      onClick={handleClick}
      className={`${styles.card} ${selected ? styles['card--selected'] : ''}`}
    >
      <div className={styles.card__row}>
        <div className={styles.card__column}>
          <h3 className={styles.card__column__label}>{getValue()}</h3>
          <p className={styles.card__column__description}>{getLabel()}</p>
        </div>
        <UserPaymentDetailsCardIndicator
          selected={selected}
          userPaymentDetails={userPaymentDetails}
        />
      </div>

      <Collapse in={selected}>
        <div className={styles.card__spacer} />
        <div
          className={`${styles.card__column} ${styles['card__column--large']}`}
        >
          <div className={styles.card__divider} />
          <UserPaymentDetailsCardStatus
            userPaymentDetails={userPaymentDetails}
          />
        </div>
      </Collapse>
    </div>
  );
}

export default memo(UserPaymentDetailsCard);
